/* ============================================================
 * 积分模块 — C 端 API
 *
 * 接口对齐后端 §十三.4（`/app/points/*`） + `/test/points/*`。
 *
 * 8 个函数：
 *   - getPointsSummary       积分账户聚合（必登）
 *   - listPointsTx           积分流水分页（必登）
 *   - getPointsPlans         充值套餐列表（匿名）
 *   - createPointsRecharge   充值下单（必登，支持 paymentMethod / merchantKey）
 *   - getPointsOrder         充值订单查询（匿名，支付页轮询）
 *   - mockPointsRecharge     dev-mock 模拟支付成功（仅 test 环境）
 *   - getAigcBalance         协同 AIGC 可用额度（必登，§13.11）
 *   - getAigcUsage           协同 AIGC 消耗明细（必登，§13.12）
 *
 * 错误统一抛 `ApiError`（`src/types/api.ts`），由 `client` 拦截器统一处理。
 * ============================================================ */

import http from '@/api/client'
import type { ApiResponse, PageResponse } from '@/types/api'
import type {
  AigcBalanceVO,
  AigcUsageItem,
  AigcUsagePage,
  AigcUsageQuery,
  PointsOrder,
  PointsOrderStatus,
  PointsPlan,
  PointsRechargeReq,
  PointsSummary,
  PointsTx,
  PointsTxQuery,
} from '@/types/points'

/** `GET /app/points/summary` 积分账户聚合（必登） */
export function getPointsSummary(): Promise<ApiResponse<PointsSummary>> {
  return http.get<ApiResponse<PointsSummary>>('/app/points/summary').then((r) => r.data)
}

/** `GET /app/points/tx` 积分流水分页（必登） */
export function listPointsTx(
  query: PointsTxQuery = {},
): Promise<PageResponse<PointsTx>> {
  return http
    .get<PageResponse<PointsTx>>('/app/points/tx', { params: query })
    .then((r) => r.data)
}

/** `GET /app/points/plans` 充值套餐列表（匿名） */
export function getPointsPlans(): Promise<ApiResponse<PointsPlan[]>> {
  return http.get<ApiResponse<PointsPlan[]>>('/app/points/plans').then((r) => r.data)
}

/** `POST /app/points/recharge` 充值下单（必登）
 *  body `{planKey, paymentMethod?, merchantKey?}`（§13.8）：
 *  `paymentMethod` 不传后端缺省 wechat；`merchantKey` 不传取该渠道默认商户。
 *  前端只透传，不写死默认值。
 */
export function createPointsRecharge(
  payload: PointsRechargeReq,
): Promise<ApiResponse<PointsOrder>> {
  const body: PointsRechargeReq = { planKey: payload.planKey }
  if (payload.paymentMethod !== undefined) body.paymentMethod = payload.paymentMethod
  if (payload.merchantKey !== undefined) body.merchantKey = payload.merchantKey

  return http
    .post<ApiResponse<PointsOrder>>('/app/points/recharge', body)
    .then((r) => r.data)
}

/** `GET /app/points/order/{orderNo}` 充值订单状态查询（匿名，支付页轮询） */
export function getPointsOrder(orderNo: string): Promise<ApiResponse<PointsOrder>> {
  return http
    .get<ApiResponse<PointsOrder>>(`/app/points/order/${orderNo}`)
    .then((r) => r.data)
}

/** 我的充值订单分页查询参数（ad41903 `/app/points/order/list`） */
export interface MyPointsOrdersParams {
  pageNum?: number
  pageSize?: number
  /** 状态筛选（可选）：PAYING/PAID/PAYMENT_FAILED；空=全部 */
  status?: PointsOrderStatus
}

/** `GET /app/points/order/list` 我的充值订单分页列表（必登，TableDataInfo 格式） */
export function listMyPointsOrders(
  params: MyPointsOrdersParams = {},
): Promise<PageResponse<PointsOrder>> {
  return http
    .get<PageResponse<PointsOrder>>('/app/points/order/list', { params })
    .then((r) => r.data)
}

/** `GET /test/points/recharge-pay-success?orderNo=xxx` 模拟支付成功（仅 test 环境）；
 *  生产环境由 `@Profile("test")` 守卫 404。 */
export function mockPointsRecharge(orderNo: string): Promise<ApiResponse<unknown>> {
  return http
    .get<ApiResponse<unknown>>('/test/points/recharge-pay-success', {
      params: { orderNo },
    })
    .then((r) => r.data)
}

/* ============================================================
 * 协同 AIGC（§13.11 + §13.12）
 *
 * 越权防护：后端强制 `external_user_id = app_{当前userId}`，
 * **前端不传任何用户标识参数**。
 *
 * `aigc-usage` 的 query 与 items 是 coaigc 原生 snake_case，
 * 本层负责与前端 camelCase 契约互转（`AigcUsageQuery` / `AigcUsageItem`）。
 * ============================================================ */

/** `GET /app/points/aigc-balance` 协同 AIGC 可用额度（必登）。
 *  coaigc 不可达时后端降级为 `code=200` + `data.error` 非空，调用方按 `error` 做兜底展示。
 */
export function getAigcBalance(): Promise<ApiResponse<AigcBalanceVO>> {
  return http.get<ApiResponse<AigcBalanceVO>>('/app/points/aigc-balance').then((r) => r.data)
}

/** coaigc 原生 usage-log（snake_case 线格式，仅本模块内部使用） */
interface AigcUsageItemRaw {
  log_id: string
  external_user_id: string
  username: string
  model_name: string
  token_name: string
  prompt_tokens: number
  completion_tokens: number
  quota: number
  use_time_seconds: number
  is_stream: boolean
  request_id: string
  created_at: string
}

interface AigcUsagePageRaw {
  items?: AigcUsageItemRaw[] | null
  total?: number
  page?: number
  page_size?: number
}

function toAigcUsageItem(raw: AigcUsageItemRaw): AigcUsageItem {
  return {
    logId: raw.log_id,
    externalUserId: raw.external_user_id,
    username: raw.username,
    modelName: raw.model_name,
    tokenName: raw.token_name,
    promptTokens: raw.prompt_tokens,
    completionTokens: raw.completion_tokens,
    quota: raw.quota,
    useTimeSeconds: raw.use_time_seconds,
    isStream: raw.is_stream,
    requestId: raw.request_id,
    createdAt: raw.created_at,
  }
}

/** camelCase 查询参数 → 后端 snake_case query（undefined 字段不下发） */
function toAigcUsageParams(query: AigcUsageQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {}
  if (query.page !== undefined) params.page = query.page
  if (query.pageSize !== undefined) params.page_size = query.pageSize
  if (query.startTimestamp !== undefined) params.start_timestamp = query.startTimestamp
  if (query.endTimestamp !== undefined) params.end_timestamp = query.endTimestamp
  return params
}

/** `GET /app/points/aigc-usage` 协同 AIGC 消耗明细（必登，§13.12）。
 *  query `page` / `page_size`（最大 100） / `start_timestamp` / `end_timestamp`（区间最长 90 天）。
 *  响应 `items` 为空 + `total=0` 属正常空态；coaigc 不可达时后端抛 `code=500`。
 */
export function getAigcUsage(
  params: AigcUsageQuery = {},
): Promise<ApiResponse<AigcUsagePage>> {
  return http
    .get<ApiResponse<AigcUsagePageRaw>>('/app/points/aigc-usage', {
      params: toAigcUsageParams(params),
    })
    .then((r) => {
      const raw = r.data.data
      return {
        ...r.data,
        data: {
          items: (raw?.items ?? []).map(toAigcUsageItem),
          total: raw?.total ?? 0,
          page: raw?.page ?? 1,
          pageSize: raw?.page_size ?? 20,
        },
      }
    })
}
